import { useState, useRef } from "react";
import {
  stringQueries,
  numberQueries,
} from "../../../config/filter/CustomFilterConfig";
import {
  CustomFilterUnion,
  CustomFilterConfig,
  CFInputItemSetting,
  CFChangeEvent,
  StringQuery,
  NumberQuery,
} from "./types";
import { f } from "../../../util/f";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { ComboBox } from "../ComboBox";
import AppliedFilterItem from "../AppliedFilterItem";
import {
  FirmaFilterInput,
  FmulinjeFilterInput,
  KursusholdFilterInput,
  PersonFilterInput,
} from "../../../interfaces/schema";
import { useToaster, Notification } from "rsuite";
import WarningIcon from "../../icons/WarningIcon";

export default function CustomFilterInputItem<
  C extends CustomFilterUnion,
>(props: {
  config: CustomFilterConfig<C>;
  filterEvent: (event: CFChangeEvent) => void;
  settings: CFInputItemSetting[] | undefined;
}) {
  // destruction!
  const { config, filterEvent, settings } = props;

  // preppings
  let queries: Array<StringQuery | NumberQuery>;

  switch (config.dataType) {
    case "string":
      queries = stringQueries;
      break;
    case "number":
      queries = numberQueries;
      break;
    default:
      queries = [];
      break;
  }

  // state things
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);

  // refs
  const inputRef = useRef<HTMLInputElement>(null);

  // for all your toasting purposes
  const toaster = useToaster();

  // handler
  function inputEventHandler() {
    let inputValue: string | number | undefined = inputRef.current?.value;

    if (!inputValue) {
      return;
    }

    if (config.dataType === "number") {
      inputValue = parseInt(inputValue);
    }

    // update filter settings
    const id: CFInputItemSetting["id"] = `${selectedQuery.query}-${inputValue}`;

    if (settings?.some((set) => set.id === id)) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon text-light bg-warning`}>
              <WarningIcon />
            </span>
            Filteret er allerede tilføjet.
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );
      return;
    }

    const settingsItem: CFInputItemSetting = {
      id: id,
      query: selectedQuery,
      value: inputValue,
    };

    let updatedSettings: CFInputItemSetting[];

    if (settings) {
      updatedSettings = [...settings, settingsItem];
    } else {
      updatedSettings = [settingsItem];
    }

    if (updatedSettings.length === 0) {
      filterEvent({
        filterString: "",
      });
      return;
    }

    const filterString = f<
      | PersonFilterInput
      | FirmaFilterInput
      | KursusholdFilterInput
      | FmulinjeFilterInput
    >()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            [config.queryKey]: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const filterObj = {
      or: updatedSettings.map((set) => {
        return {
          [config.queryKey]: {
            [set.query.query]: set.value,
          },
        };
      }),
    };

    filterEvent({
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: updatedSettings,
    });
  }

  function removeFilterEventHandler(id: string) {
    const filteredSettings = settings?.filter((set) => set.id !== id);

    if (filteredSettings?.length === 0) {
      filterEvent({
        filterString: "",
        filterObj: {},
        filterSettings: undefined,
      });
      return;
    }

    const filterString = f<
      | PersonFilterInput
      | FirmaFilterInput
      | KursusholdFilterInput
      | FmulinjeFilterInput
    >()
      .addFilter(
        "or",
        filteredSettings?.map((set) => {
          return {
            [config.queryKey]: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const filterObj = {
      or: filteredSettings?.map((set) => {
        return {
          [config.queryKey]: {
            [set.query.query]: set.value,
          },
        };
      }),
    };

    filterEvent({
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: filteredSettings,
    });
  }

  return (
    <div className="c-custom-filter__content">
      <div className="c-applied-filter-group">
        {settings?.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.query.displayName}`}
            label={`${set.value}`}
            removeFilterHandler={() => {
              const id: CFInputItemSetting["id"] = `${set.query.query}-${set.value}`;
              removeFilterEventHandler(id);
            }}
            key={`${set.query.query}-${set.value}`}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <input
          className="form-control"
          placeholder={`${config.displayName}`}
          ref={inputRef}
          type={config.dataType === "string" ? "text" : "number"}
          required
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              inputEventHandler();
            }
          }}
        />

        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            inputEventHandler();
          }}
        >
          OK
        </button>
      </ComboBox>
    </div>
  );
}
